import { useCallback, useEffect, useState } from 'react';
import { Alert, Loading, PageHeader, Pager } from '../App';
import { adminApi, dateTime, errorMessage, humanise, statusVariant, type AdminReport, type PagedResult } from '../api/admin';

export function ReportsPage() {
  const [data, setData] = useState<PagedResult<AdminReport> | null>(null);
  const [status, setStatus] = useState('Open');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resolving, setResolving] = useState<AdminReport | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setData(await adminApi.reports({ status: status || undefined, page, pageSize: 20 }));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [status, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHeader
        title="Reported listings"
        description="Complaints raised by customers. Resolving a report does not change the listing — suspend it separately if needed."
      />

      <Alert message={error} />
      <Alert message={success} variant="success" />

      <div className="admin-card mb-3 d-flex flex-wrap gap-2 align-items-center">
        <span className="small text-muted">Show:</span>
        {['Open', 'UnderReview', 'ActionTaken', 'Dismissed', ''].map((value) => (
          <button
            key={value || 'all'}
            type="button"
            className={`btn btn-sm ${status === value ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => {
              setPage(1);
              setStatus(value);
            }}
          >
            {value ? humanise(value) : 'All'}
          </button>
        ))}
      </div>

      {!data ? (
        <Loading rows={5} />
      ) : data.items.length === 0 ? (
        <div className="admin-card text-muted">Nothing in this queue.</div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">Listing</th>
                  <th scope="col">Complaint</th>
                  <th scope="col">Reported by</th>
                  <th scope="col">When</th>
                  <th scope="col">Status</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((report) => (
                  <tr key={report.id}>
                    <td>
                      <div className="fw-semibold">{report.propertyTitle}</div>
                      <span className={`badge bg-${statusVariant(report.propertyStatus)}`}>
                        {humanise(report.propertyStatus)}
                      </span>
                    </td>

                    <td>
                      <div className="fw-semibold">{humanise(report.reason)}</div>
                      {report.details && <div className="text-muted small">{report.details}</div>}
                    </td>

                    <td className="small text-muted">
                      {report.reporterName ?? 'Anonymous'}
                      {report.reporterContact && <div>{report.reporterContact}</div>}
                    </td>

                    <td className="small text-muted text-nowrap">{dateTime(report.createdAtUtc)}</td>

                    <td>
                      <span className={`badge bg-${report.status === 'Open' ? 'warning' : 'secondary'}`}>
                        {humanise(report.status)}
                      </span>
                      {report.resolutionNotes && <div className="text-muted small mt-1">{report.resolutionNotes}</div>}
                    </td>

                    <td>
                      {report.status === 'Open' || report.status === 'UnderReview' ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setResolving(report)}
                        >
                          Resolve
                        </button>
                      ) : (
                        <span className="text-muted small">{dateTime(report.resolvedAtUtc)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager result={data} onPage={setPage} />
        </>
      )}

      {resolving && (
        <ResolveDialog
          report={resolving}
          onCancel={() => setResolving(null)}
          onDone={async (message) => {
            setResolving(null);
            setSuccess(message);
            await load();
          }}
          onError={setError}
        />
      )}
    </>
  );
}

function ResolveDialog({
  report,
  onCancel,
  onDone,
  onError,
}: {
  report: AdminReport;
  onCancel: () => void;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [status, setStatus] = useState('ActionTaken');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);

    try {
      await adminApi.resolveReport(report.id, status, notes.trim() || undefined);
      await onDone('Report resolved.');
    } catch (err) {
      onError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="resolve-title">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title h5" id="resolve-title">
                Resolve this report
              </h2>
            </div>

            <div className="modal-body">
              <p className="mb-3">
                <strong>{report.propertyTitle}</strong>
                <br />
                <span className="text-muted small">{humanise(report.reason)}</span>
              </p>

              <div className="mb-3">
                <label className="form-label" htmlFor="resolve-status">
                  Outcome
                </label>
                <select
                  id="resolve-status"
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="UnderReview">Still looking into it</option>
                  <option value="ActionTaken">Action taken</option>
                  <option value="Dismissed">Dismissed, no action needed</option>
                </select>
              </div>

              <div className="mb-0">
                <label className="form-label" htmlFor="resolve-notes">
                  Notes
                </label>
                <textarea
                  id="resolve-notes"
                  className="form-control"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={2000}
                />
              </div>

              <p className="form-text mb-0 mt-2">
                This records the decision only. To take the listing down, suspend it from the moderation page.
              </p>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
